import type { ExerciseResult, SessionResult } from "./schemas/sessionResult.js";
import { type BodyMapValue, volumeBySlugToHeatmap } from "./bodymap.js";
import type { HeatmapRamp } from "./themes/types.js";
import {
  muscleShortLabel,
  type CatalogMuscleSlug,
} from "./muscleCatalog.js";
import {
  catalogVolumeToBodymap,
  normalizeVolumeByMuscle,
} from "./volumeByMuscle.js";
import {
  formatE1rm,
  formatVolume,
  formatWeight,
  volumeReadsZero,
  UNTRANSLATED_UNIT_LABELS,
  type WeightUnitLabels,
} from "./units.js";
import {
  buildFooterStats,
  formatDuration,
} from "./shareCardFormat.js";
import type { WorkoutCalorieSource } from "./resolveWorkoutCalories.js";
import type { CalorieMethod } from "./calorieEstimate.js";

// ⚠️ The hardcoded CARD_TOKENS design tokens that used to live here (pinned
// values from JL-postsession-summary-spec.md v0.3 §4.2, superseded on
// 2026-07-01 when the card started resolving colours from the caller's
// theme) painted only apps/web's legacy card. Deleted with RW-14 on
// 2026-09-24 (recovery SHA 7ae8e35e), its only caller.

export type ShareCardData = {
  // Top derived label ("QUAD · POSTERIOR"). Comes verbatim from
  // SessionResult.sessionLabel; here only to keep the renderer pure.
  sessionLabel: string;
  // The big number — either the headline PR E1RM or the total session
  // volume when no PR happened. Formatted with unit suffix.
  dominantValue: string;
  // Label under the dominant number, e.g. "Squat E1RM" or "Total Volume".
  dominantLabel: string;
  // True when the dominant number corresponds to a new E1RM PR. Drives
  // the "NEW PR" badge on the share card next to the delta. Always false
  // when the fallback "Total Volume" headline is in play.
  dominantIsPr: boolean;
  // Optional delta badge ("+3 kg", direction informs colour).
  delta:
    | {
        value: string;
        direction: "up" | "down" | "neutral";
      }
    | null;
  // Top muscle bars (3–4 in the layout). Bars are normalised against
  // the heaviest-volume muscle in this session.
  muscleBars: ReadonlyArray<{
    slug: CatalogMuscleSlug;
    label: string;
    fillPct: number; // 0..1
    volumeText: string;
  }>;
  // Top lifts of the session — exercise name + the topSet that
  // produced the e1RM (kg × reps) + the e1RM itself. 4 rows max; the
  // recipient gets enough specifics to discuss the session without
  // having to ask "what reps?". PR rows flagged so the renderer can
  // pin a trophy.
  topLifts: ReadonlyArray<{
    slug: string;
    name: string;
    topSetText: string; // e.g. "77.5 kg × 5"
    // The set's e1RM when it qualified (reps within the Epley cap), else
    // null — high-rep sets have no trustworthy e1RM, so the renderer
    // simply omits the secondary number and the lift still ranks by load.
    e1rmText: string | null; // e.g. "90.4 kg"
    isPr: boolean;
  }>;
  // Bodymap paint map for the dual front+back figure.
  bodyMapValue: BodyMapValue;
  // Structured footer STAT-STRIP (HC-8 B3): one labelled cell per stat —
  // Volume · Duration · Density · [TUT] · [Impulse] · [Calories]. Each cell is
  // value-over-label; absent stats are omitted. Calories comes from the
  // resolved energy the caller passes in, and is itself conditional (LEGAL-5:
  // omitted whenever its provenance touches Health Connect) — the card is a
  // STATIC image, so no (i) provenance pill, just the canonical number.
  footerStats: ReadonlyArray<{ label: string; value: string }>;
  // ISO date string already localised (e.g. "Tue 03 Jun 2026"). The
  // renderer doesn't format — keeps platform locale lookups out of the
  // pure transform.
  date: string;
  // From Profile.socialHandle. Null = render nothing in the footer's
  // handle slot.
  socialHandle: string | null;
};

// Resolved energy the caller feeds into the share card's stat-strip (HC-8 B3).
// Pre-resolved by the caller (same `sessionEnergyReadout`/`resolveWorkoutCalories`
// the on-screen block uses) so this transform stays a pure formatter — no calorie
// math here. Absent fields → the matching cell is simply omitted.
export type ShareCardEnergy = {
  /** Canonical resolved kcal, or null/absent when the session carries none. */
  kcal?: number | null;
  /** Which figure `kcal` is (LEGAL-5, 2026-09-09): the share card is an artifact
   *  that leaves the app, so a value read FROM Health Connect never renders on
   *  it — Google's policy bars publicly sharing sensitive health data without
   *  explicit per-share consent. JL's own estimate is our data and stays.
   *  `"hc"` omits the Calories cell entirely; anything else (incl. absent)
   *  shows it, matching `resolveWorkoutCalories`'s `ResolvedWorkoutCalories.source`. */
  kcalSource?: WorkoutCalorieSource | null;
  /** How `kcal` was computed when it's the JL estimate (LEGAL-5, 2026-09-09):
   *  `estimateBoutCalories` picks "keytel" (Keytel HR formula) whenever a
   *  measured avgHrBpm is present, and that HR has exactly one source — Health
   *  Connect (manual HR entry is excluded, MSC-3). So a `"keytel"` estimate is
   *  Health-Connect-derived even though `kcalSource` reads `"jl:estimate"`;
   *  only `"met"` (weight × duration × MET, no HR) is genuinely ours. */
  kcalMethod?: CalorieMethod | null;
  /** Ripiego (titolare, 2026-09-09): a MET-only twin of `kcal`, computed by the
   *  caller with HR stripped (same estimator, calorieEstimate.ts's own MET
   *  route — not a second engine). The strip shows THIS number instead of
   *  `kcal` whenever `kcal`'s provenance touches Health Connect
   *  (`kcalSource === "hc"` or `kcalMethod === "keytel"`); null/absent when
   *  the caller had no raw per-set ingredients to recompute it, in which case
   *  the Calories cell is simply omitted (same as before this field existed).
   *  Different number from the on-screen canonical figure by design — the two
   *  surfaces answer different questions (measured/best-available vs.
   *  shareable-without-HC). */
  metFallbackKcal?: number | null;
};

// User-facing strings the share card composition path renders directly
// into the PNG. shareCard.ts has no react-i18next dependency, so the
// caller passes a labels object resolved against the active locale.
// Defaults are English so any caller that omits the labels still gets a
// rendering identical to pre-2026-05-23 behaviour.
export type ShareCardLabels = {
  durationMinute: string;       // "min" — unit suffix when duration < 1h
  durationHour: string;         // "h" — hour suffix
  durationMinuteShort: string;  // "m" — minute suffix in "Xh Ym"
  footerDensitySuffix: string;  // "/min" — appended after density value
  fallbackLabel: string;        // "Total Volume" — dominant when no PR
  timeLabel: string;            // "Total Time" — dominant when volume is 0
  tutLabel: string;             // "TUT" — time-under-tension footer suffix + strip cell title
  impulseUnit: string;          // "kg·s" — impulse footer unit
  e1rmSuffix: string;           // "E1RM" — appended to the PR label
  // The weight unit as a word (CN-26 wave 2). ⛔ NOT the same thing as
  // `impulseUnit`: "kg·s" is a physical symbol that reads identically in all
  // five languages, while a Russian writes «кг» and «фунт». The share card is
  // an image an athlete posts, so an untranslated suffix on it is the most
  // public place this defect could show.
  units: WeightUnitLabels;
  // Stat-strip cell titles (HC-8 B3). Reuse the in-app postSession block keys at
  // the callsite so no new i18n strings are minted.
  volumeLabel: string;          // "Volume"
  durationLabel: string;        // "Duration"
  densityLabel: string;         // "Density"
  impulseLabel: string;         // "Impulse" — cell title (impulseUnit is the value's unit)
  caloriesLabel: string;        // "Calories"
};

export const DEFAULT_SHARE_CARD_LABELS: ShareCardLabels = {
  durationMinute: "min",
  durationHour: "h",
  durationMinuteShort: "m",
  footerDensitySuffix: "/min",
  fallbackLabel: "Total Volume",
  timeLabel: "Total Time",
  tutLabel: "TUT",
  impulseUnit: "kg·s",
  e1rmSuffix: "E1RM",
  volumeLabel: "Volume",
  durationLabel: "Duration",
  densityLabel: "Density",
  impulseLabel: "Impulse",
  caloriesLabel: "Calories",
  // ⛔ The untranslated pair is the DEFAULT only because every field in this
  // bundle has an English default and the mobile caller overrides the whole
  // thing through `t()`. A card rendered without that override is a test
  // fixture, not a screen.
  units: UNTRANSLATED_UNIT_LABELS,
};

// Ranking score for the "Top Lifts" surfaces (share card + post-session
// card). Prefers the set's e1RM when it qualified (reps within the Epley
// cap), and falls back to the absolute top-set load otherwise. The
// fallback is what keeps heavy high-rep compounds — which never get an
// e1RM past the rep cap — in the ranking instead of being filtered out
// (2026-06-07 bug). Returns 0 for exercises with no weighted top set
// (pure cardio / pure bodyweight), which the callers exclude upfront.
export const liftScore = (er: ExerciseResult): number =>
  er.topE1rmKg ?? er.topSet?.weightKg ?? 0;

// Build the renderer input from a SessionResult + the few extra fields
// the renderer needs (handle, unit, locale). Pure — no clock reads, no
// platform calls. The caller passes a pre-formatted date string so we
// don't fight platform locale APIs here.
//
// `resolveName` is the FK→localised-name resolver from the caller. Per
// [[feedback_no_denormalized_display_snapshots]] we never read the
// SessionResult's exerciseName snapshot for display; it's only used as
// the deleted-row fallback inside the resolver itself.
export function sessionResultToShareCardData(input: {
  result: SessionResult;
  socialHandle: string | null;
  unit: "kg" | "lb";
  locale: string;
  // Pre-formatted date string for the card header. Passed in because
  // mobile + web have different locale-formatting paths.
  dateLabel: string;
  // (slug, snapshotFallback) → localised display name. Required so
  // share PNGs render in the user's active language even when older
  // SessionResult rows carry a stale EN snapshot.
  resolveName: (slug: string, snapshotFallback?: string) => string;
  // Localised user-facing strings rendered into the PNG. Optional for
  // backward compatibility; callers without useTranslation in scope can
  // omit and ship the English defaults.
  labels?: Partial<ShareCardLabels>;
  // Resolved calories + effort for the footer stat-strip (HC-8 B3). Optional:
  // omit and the Calories/Effort cells are simply absent.
  energy?: ShareCardEnergy;
  // The caller's theme-resolved 5-step heatmap ramp, for the bodymap figures.
  // REQUIRED (DS-77): with a default here the card painted the old family's
  // orange under every theme, next to a legend drawn from the real one.
  heatmapRamp: HeatmapRamp;
}): ShareCardData {
  const {
    result,
    socialHandle,
    unit,
    locale,
    dateLabel,
    resolveName,
    energy,
    heatmapRamp,
  } = input;
  const labels: ShareCardLabels = { ...DEFAULT_SHARE_CARD_LABELS, ...(input.labels ?? {}) };

  // Dominant number: highest-delta PR's E1RM if any exercise is a PR;
  // otherwise the session's total volume.
  const headlinePR = result.exerciseResults.find(
    (er) => er.isPrE1rm && er.topE1rmKg !== null,
  );
  const sortedPRs = result.exerciseResults
    .filter((er) => er.isPrE1rm && er.topE1rmKg !== null && er.topE1rmKg > 0)
    .sort((a, b) => (b.deltaE1rmKg ?? 0) - (a.deltaE1rmKg ?? 0));
  const topPR = sortedPRs[0] ?? headlinePR ?? null;

  let dominantValue: string;
  let dominantLabel: string;
  let dominantIsPr = false;
  let delta: ShareCardData["delta"] = null;
  if (topPR && topPR.topE1rmKg !== null) {
    dominantValue = formatE1rm(topPR.topE1rmKg, unit, locale, labels.units);
    dominantLabel = `${resolveName(topPR.exerciseSlug, topPR.exerciseName)} ${labels.e1rmSuffix}`;
    dominantIsPr = topPR.isPrE1rm;
    if (topPR.deltaE1rmKg !== null && topPR.deltaE1rmKg !== 0) {
      const sign = topPR.deltaE1rmKg > 0 ? "+" : "−";
      delta = {
        value: `${sign}${formatE1rm(Math.abs(topPR.deltaE1rmKg), unit, locale, labels.units)}`,
        direction: topPR.deltaE1rmKg > 0 ? "up" : "down",
      };
    }
  } else {
    // No PR. Normally the session's total volume is the headline — but a
    // hold-only warmup (all timed sets → excluded from volume) or a
    // weight-less machine day computes 0 kg, and a big "0 Kg" reads as "did
    // nothing" (Antenore 2026-06-09). When there's no weighted volume, fall
    // back to the session's total time so the headline still reflects the
    // work done. Volume stays the headline whenever it's non-zero.
    const dur = formatDuration(result.durationMinutes, labels);
    if (
      (result.totalVolumeKg <= 0 || volumeReadsZero(result.totalVolumeKg, unit)) &&
      dur
    ) {
      dominantValue = dur;
      dominantLabel = labels.timeLabel;
    } else {
      dominantValue = formatVolume(result.totalVolumeKg, unit, locale, labels.units);
      dominantLabel = labels.fallbackLabel;
    }
  }

  // Muscle bars — top 4 by volume (was 5; trimmed to free vertical
  // budget for the new top-lifts block above). Slice #4: read catalog-
  // vocab keys via normalizeVolumeByMuscle so legacy stored rows
  // (bodymap keys) also render with catalog labels.
  const catalogVolume = normalizeVolumeByMuscle(result.volumeByMuscle);
  const muscleEntries = (
    Object.entries(catalogVolume) as Array<[CatalogMuscleSlug, number]>
  )
    .filter(([, v]) => typeof v === "number" && v > 0)
    .sort((a, b) => b[1] - a[1]);
  const topMuscles = muscleEntries.slice(0, 4);
  const maxMuscleVolume = topMuscles[0]?.[1] ?? 0;
  const muscleBars = topMuscles.map(([slug, volume]) => ({
    slug,
    // Brand-style short tag (EN-only). The share PNG is an artifact
    // distributed externally; consistent EN labels keep the brand
    // surface stable. In-app surfaces use t(muscleKey(slug)) instead.
    label: muscleShortLabel(slug),
    fillPct: maxMuscleVolume > 0 ? volume / maxMuscleVolume : 0,
    volumeText: formatVolume(volume, unit, locale, labels.units),
  }));

  // Top lifts — every exercise with a weighted top set, ranked by e1RM
  // when the set qualified (reps within the Epley cap) and otherwise by
  // the absolute top-set load. The load fallback is the fix for the
  // 2026-06-07 bug: on a high-rep day every compound runs past the Epley
  // rep cap → no set gets an e1RM, so the old `topE1rmKg !== null` filter
  // dropped all the heavy lifts and surfaced only a light accessory that
  // happened to fall under the cap (a 5 kg wrist extension beating a
  // 70 kg cable pull-through). e1RM stays the ranking signal when present
  // (it normalises across rep ranges); absolute load is the bug-proof
  // floor that keeps every real lift in the running. Capped at 4 rows so
  // the card stays scannable on 9:16.
  const topLifts = result.exerciseResults
    .filter((er) => er.topSet !== null && er.topSet.weightKg > 0)
    .sort((a, b) => liftScore(b) - liftScore(a))
    .slice(0, 4)
    .map((er) => ({
      slug: er.exerciseSlug,
      name: resolveName(er.exerciseSlug, er.exerciseName),
      topSetText: `${formatWeight(er.topSet!.weightKg, unit, locale, labels.units)} × ${er.topSet!.reps}`,
      e1rmText:
        er.topE1rmKg !== null ? formatE1rm(er.topE1rmKg, unit, locale, labels.units) : null,
      isPr: er.isPrE1rm,
    }));

  // Bodymap paint reads the bodymap-keyed shape; bridge the catalog-
  // vocab volume map back through catalogVolumeToBodymap so the
  // anatomical heatmap stays the visual it always was. Volume math
  // itself stays in catalog vocab end-to-end.
  const bodyMapValue = volumeBySlugToHeatmap(
    catalogVolumeToBodymap(catalogVolume),
    heatmapRamp,
  );

  const footerStats = buildFooterStats(
    result.durationMinutes,
    result.totalVolumeKg,
    result.densityKgPerMin,
    result.totalTutSec ?? 0,
    result.totalImpulseKgS ?? 0,
    energy,
    unit,
    locale,
    labels,
  );

  return {
    sessionLabel: result.sessionLabel,
    dominantValue,
    dominantLabel,
    dominantIsPr,
    delta,
    muscleBars,
    topLifts,
    bodyMapValue,
    footerStats,
    date: dateLabel,
    socialHandle,
  };
}
